import { cn } from "@newxsystems/shared";
import { getKaajSectionSpacing } from "../../../../modules/layout/kaaj-section-spacing";
import { KaajAction } from "../_shared/KaajAction";
import { LogoProofCarousel } from "./LogoProofCarousel.client";
import type { ILogoProofBlockProps } from "./LogoProofBlock.props";
import { RichTextHtml } from "../../RichTextHtml";

export const LogoProofBlock = (props: ILogoProofBlockProps) => {
  const { parentClass, eyebrow, heading, spacing, subheading, caption } = props;
  const logos = props.logos ?? [];
  const actions = props.buttons ?? [];
  const sectionId = props.anchorId ?? undefined;
  const headingId = heading && sectionId ? `${sectionId}-heading` : undefined;
  const hasIntro = Boolean(eyebrow || heading || subheading);
  const hasLogos = logos.length > 0;
  const hasCarousel = logos.length > 5;
  const hasLogoField = hasLogos || Boolean(caption);

  if (!hasIntro && !hasLogoField && actions.length === 0) {
    return null;
  }

  return (
    <section
      className={cn(
        "relative isolate overflow-hidden text-[#151927]",
        getKaajSectionSpacing(spacing),
        parentClass,
      )}
      data-kaaj-block="logoProof"
      id={sectionId}
      {...(headingId ? { "aria-labelledby": headingId } : {})}
    >
      <div className="kaaj-container relative">
        {hasIntro ? (
          <div className="mx-auto mb-9 flex max-w-4xl flex-col items-center gap-4 text-center lg:mb-12">
            {eyebrow ? (
              <p className="inline-flex max-w-full rounded-full border border-[#CAD7FF] bg-white px-4 py-1.5 text-xs font-extrabold uppercase tracking-[0.18em] text-[#2144FF] shadow-[0_10px_24px_rgba(33,68,255,0.08)] [overflow-wrap:anywhere]">
                {eyebrow}
              </p>
            ) : null}

            {heading ? (
              <h2 className="kaaj-section-heading max-w-4xl text-[#151927]" id={headingId}>
                {heading}
              </h2>
            ) : null}

            {subheading ? (
              <RichTextHtml
                className="mx-auto max-w-2xl text-pretty text-base leading-7 text-[#4B5568] [overflow-wrap:anywhere] sm:text-lg sm:leading-8"
                html={subheading}
              />
            ) : null}
          </div>
        ) : null}

        {hasLogoField ? (
          <div className="relative mx-auto">
            {!hasCarousel ? (
              <div
                aria-hidden="true"
                className="pointer-events-none absolute -inset-x-5 -top-7 h-24 rounded-full bg-[radial-gradient(circle_at_20%_50%,rgba(217,225,255,0.64),transparent_36%),radial-gradient(circle_at_78%_50%,rgba(255,234,249,0.52),transparent_38%)] blur-2xl"
              />
            ) : null}

            <div
              className={cn(
                "relative overflow-hidden py-3 sm:py-4 lg:py-4",
                hasCarousel ? "[contain:layout_paint]" : "",
              )}
            >
              <span
                aria-hidden="true"
                className="pointer-events-none absolute inset-x-8 top-0 h-px bg-gradient-to-r from-transparent via-[#8FA3FF] to-transparent"
              />

              {hasLogos ? <LogoProofCarousel logos={logos} /> : null}

              {caption ? (
                <p
                  className={cn(
                    "mx-auto max-w-3xl text-pretty text-sm font-semibold leading-6 text-[#4B5568] [overflow-wrap:anywhere] sm:text-center",
                    hasLogos ? "mt-5 border-t border-[#E6EBFF] px-1 pt-5" : undefined,
                  )}
                >
                  {caption}
                </p>
              ) : null}
            </div>
          </div>
        ) : null}

        {actions.length > 0 ? (
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3 max-[420px]:flex-col max-[420px]:items-stretch">
            {actions.map((action, index) => (
              <KaajAction
                ariaLabel={action.ariaLabel}
                className="max-[420px]:w-full"
                href={action.href}
                key={`${action.href}-${action.text}-${index}`}
                label={action.text}
                target={action.target}
                variant={action.style}
              />
            ))}
          </div>
        ) : null}
      </div>
    </section>
  );
};

LogoProofBlock.displayName = "Block::LogoProofBlock";
